import { useState } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import { useInViewOnce } from './useInViewOnce'
import MediaLightbox from './MediaLightbox'
import RevealSection from './RevealSection'

const EASE = [0.16, 1, 0.3, 1]

function pad(value) {
  return String(value).padStart(2, '0')
}

/**
 * Photo shoot as a contact sheet: several frames are visible at once on every screen size.
 * Desktop: with five or more frames the first one is featured (2x2) next to a grid of the rest.
 * Mobile: a two-column grid, so at least four frames fit on the first screen.
 */
function ShootSection({ images, labels, heroImage, cardName }) {
  const [ref, nearViewport] = useInViewOnce()
  const reduceMotion = useReducedMotion()
  const [openIndex, setOpenIndex] = useState(null)
  const total = images.length

  if (total < 2) return null

  const featuredLayout = total >= 5
  const desktopColumns =
    total === 2 ? 'lg:grid-cols-2' : total === 3 ? 'lg:grid-cols-3' : 'lg:grid-cols-4'

  const labelFor = (index) => labels?.[index] ?? (images[index] === heroImage ? 'Front' : void 0)
  const media =
    openIndex == null
      ? null
      : {
          kind: 'image',
          url: images[openIndex],
          alt: `${cardName} ${labelFor(openIndex)?.toLowerCase() ?? `frame ${openIndex + 1}`}`,
          caption: labelFor(openIndex),
        }
  const page = (delta) =>
    setOpenIndex((index) => (index == null ? index : (index + delta + total) % total))

  const container = {
    hidden: {},
    show: { transition: { staggerChildren: reduceMotion ? 0 : 0.07 } },
  }
  const tile = {
    hidden: { opacity: 0, y: reduceMotion ? 0 : 24 },
    show: { opacity: 1, y: 0, transition: { duration: reduceMotion ? 0.2 : 0.7, ease: EASE } },
  }

  return (
    <>
      <RevealSection
        eyebrow="The shoot"
        title={`${total} frames, straight from the vault`}
        subtitle="Every photo the grader published. Tap any frame to inspect it up close."
      >
        <div ref={ref}>
          <motion.div
            variants={container}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.1 }}
            className={`grid grid-cols-2 gap-3 sm:gap-4 ${desktopColumns} max-lg:[&>*:last-child:nth-child(odd)]:col-span-2`}
            role="list"
          >
            {images.map((image, index) => {
              const label = labelFor(index)
              const featured = featuredLayout && index === 0
              const lonelyLast = index === total - 1 && total % 2 === 1
              return (
                <motion.button
                  key={image}
                  variants={tile}
                  type="button"
                  role="listitem"
                  onClick={() => setOpenIndex(index)}
                  aria-label={label ? `Inspect ${label}` : `Inspect frame ${index + 1}`}
                  className={`m1nt-plate group flex min-w-0 flex-col p-3 text-left focus:outline-none focus-visible:border-m1-violet sm:p-4 ${
                    featured ? 'lg:col-span-2 lg:row-span-2' : ''
                  }`}
                >
                  <div
                    className={`relative min-h-0 overflow-hidden rounded-lg bg-m1-ground/60 ${
                      featured
                        ? 'aspect-[5/7] lg:aspect-auto lg:flex-1'
                        : lonelyLast
                          ? 'aspect-[5/7] max-lg:aspect-[16/11]'
                          : 'aspect-[5/7]'
                    }`}
                  >
                    {nearViewport && (
                      <img
                        loading="lazy"
                        src={image}
                        alt={
                          label
                            ? `${cardName} ${label.toLowerCase()}`
                            : `${cardName} frame ${index + 1}`
                        }
                        draggable={false}
                        className="absolute inset-0 h-full w-full object-contain p-1 transition-transform duration-700 ease-out group-hover:scale-[1.04] sm:p-2"
                      />
                    )}
                  </div>
                  <div className="mt-3 flex items-baseline justify-between gap-3">
                    <span
                      className={`min-w-0 truncate ${
                        label
                          ? 'font-m1-display text-[17px] text-m1-ink sm:text-[20px]'
                          : 'font-m1-mono text-[13px] text-m1-muted'
                      }`}
                    >
                      {label ?? `Frame ${pad(index + 1)}`}
                    </span>
                    <span className="shrink-0 font-m1-mono text-[12px] tabular-nums text-m1-muted transition-colors group-hover:text-m1-violet">
                      {pad(index + 1)}
                    </span>
                  </div>
                </motion.button>
              )
            })}
          </motion.div>
        </div>
      </RevealSection>

      <MediaLightbox
        media={media}
        immersive
        tilt
        onClose={() => setOpenIndex(null)}
        onPrev={() => page(-1)}
        onNext={() => page(1)}
        position={openIndex == null ? void 0 : { index: openIndex, total }}
      />
    </>
  )
}

export { ShootSection as default }
